import React, { useMemo, useState } from 'react';
import { Box, Typography, Alert, TextField, MenuItem, IconButton, Tooltip, Fab, Chip } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useNavigate } from 'react-router-dom';
import * as Icons from '../constants/icons';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';
import { STANDARD_FAB_CTA_SX } from '../constants/buttonStyles';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useAuth } from '../firebase/AuthProvider';
import {
  useMailCampaignList,
  mailApi,
  MailCampaignRow,
  MailCampaignStatus,
  CAMPAIGN_STATUS_LABEL,
  fmtDateTime,
  fmtCountRate,
} from '../hooks/useMailDelivery';
import { CampaignStatusChip } from '../components/mailDelivery/MailStatusChip';

/**
 * メール配信 配信履歴 (/mail-delivery)。
 *
 * ブラストメールの「配信履歴」に相当。下書き・予約・送信中・完了をひとつの一覧で見せ、
 * 行操作で 詳細 / 編集 (下書き・予約のみ) / 複製 (次週分の作成) / 予約取消 / 下書き削除 を行う。
 * 新規配信は右下の Fab から配信作成 (/mail-delivery/campaigns/new) へ。
 */
const MailDeliveryCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotification();
  const confirm = useConfirm();
  const { user } = useAuth();
  const operator = user?.email || 'unknown';
  const list = useMailCampaignList();
  const [qInput, setQInput] = useState('');

  const handleCopy = async (row: MailCampaignRow) => {
    const r = await mailApi.copyCampaign(row.id, operator);
    if (!r.ok) return notify.error(r.error);
    notify.success('複製して下書きを作成しました');
    navigate(`/mail-delivery/campaigns/${r.data.id}/edit`);
  };

  const handleCancel = async (row: MailCampaignRow) => {
    const ok = await confirm({
      title: '予約の取消',
      message: `「${row.subject}」の配信予約を取り消しますか？`,
      confirmText: '取り消す',
      destructive: true,
    });
    if (!ok) return;
    const r = await mailApi.cancelCampaign(row.id, operator);
    if (!r.ok) return notify.error(r.error);
    notify.success('予約を取り消しました');
    list.reload();
  };

  const handleDelete = async (row: MailCampaignRow) => {
    const ok = await confirm({
      title: '下書きの削除',
      message: `下書き「${row.subject}」を削除しますか？`,
      confirmText: '削除する',
      destructive: true,
    });
    if (!ok) return;
    const r = await mailApi.deleteDraft(row.id);
    if (!r.ok) return notify.error(r.error);
    notify.success('下書きを削除しました');
    list.reload();
  };

  const columns: Column[] = useMemo(
    () => [
      { field: 'id', headerName: 'No', width: 60, align: 'right' },
      {
        field: 'subject',
        headerName: '件名',
        width: 320,
        renderCell: (row: MailCampaignRow) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            {row.legacy_no != null && (
              <Tooltip title={`ブラストメールから移行 (配信番号 ${row.legacy_no}、件数のみ)`}>
                <Chip size="small" variant="outlined" label="移行" sx={{ fontSize: 10.5, height: 18 }} />
              </Tooltip>
            )}
            <Typography
              component="span"
              noWrap
              onClick={() => navigate(`/mail-delivery/campaigns/${row.id}`)}
              sx={{ fontSize: 13, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              {row.subject}
            </Typography>
          </Box>
        ),
      },
      { field: 'list_names', headerName: '配信リスト', width: 180 },
      {
        field: 'from_email',
        headerName: '送信元',
        width: 200,
        renderCell: (row: MailCampaignRow) => row.from_display_name || row.from_email || '',
      },
      {
        field: 'scheduled_at',
        headerName: '配信日時',
        width: 140,
        renderCell: (row: MailCampaignRow) => fmtDateTime(row.started_at || row.scheduled_at),
      },
      { field: 'status', headerName: '状態', width: 80, renderCell: (row: MailCampaignRow) => <CampaignStatusChip status={row.status} /> },
      { field: 'total_count', headerName: '配信数', width: 80, align: 'right', renderCell: (r: MailCampaignRow) => (r.total_count || 0).toLocaleString() },
      {
        field: 'delivered_count',
        headerName: '成功',
        width: 110,
        align: 'right',
        renderCell: (r: MailCampaignRow) => fmtCountRate(r.delivered_count, r.total_count),
      },
      {
        field: 'bounce_count',
        headerName: 'エラー',
        width: 100,
        align: 'right',
        renderCell: (r: MailCampaignRow) => fmtCountRate((r.bounce_count || 0) + (r.failed_count || 0), r.total_count),
      },
      { field: 'open_count', headerName: '開封', width: 100, align: 'right', renderCell: (r: MailCampaignRow) => fmtCountRate(r.open_count, r.total_count) },
      { field: 'click_count', headerName: 'クリック', width: 80, align: 'right', renderCell: (r: MailCampaignRow) => (r.click_count || 0).toLocaleString() },
      {
        field: '_actions',
        headerName: '操作',
        width: 170,
        renderCell: (row: MailCampaignRow) => {
          const editable = row.status === 'draft' || row.status === 'scheduled';
          return (
            <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="詳細">
                <IconButton size="small" onClick={() => navigate(`/mail-delivery/campaigns/${row.id}`)}>
                  <Icons.Visibility sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={editable ? '編集' : '送信済みの配信は編集できません'}>
                <span>
                  <IconButton size="small" disabled={!editable} onClick={() => navigate(`/mail-delivery/campaigns/${row.id}/edit`)}>
                    <Icons.Edit sx={{ fontSize: 17 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="複製して新しい配信を作る">
                <IconButton size="small" onClick={() => handleCopy(row)}>
                  <Icons.ContentCopy sx={{ fontSize: 17 }} />
                </IconButton>
              </Tooltip>
              {row.status === 'scheduled' && (
                <Tooltip title="予約を取り消す">
                  <IconButton size="small" color="warning" onClick={() => handleCancel(row)}>
                    <Icons.Cancel sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}
              {row.status === 'draft' && (
                <Tooltip title="下書きを削除">
                  <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                    <Icons.Delete sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate, operator],
  );

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 2, width: '96%', margin: '0 auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Icons.EmailOutlined sx={{ color: blueGrey[600] }} />
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: blueGrey[700], mr: 2 }}>メール配信 配信履歴</Typography>
        <TextField
          select
          size="small"
          label="状態"
          value={list.status}
          onChange={(e) => list.changeStatus(e.target.value as MailCampaignStatus | '')}
          sx={{ width: 140, '& .MuiInputBase-input': { fontSize: 13 } }}
        >
          <MenuItem value="">すべて</MenuItem>
          {(Object.keys(CAMPAIGN_STATUS_LABEL) as MailCampaignStatus[]).map((s) => (
            <MenuItem key={s} value={s}>
              {CAMPAIGN_STATUS_LABEL[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="件名で検索"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') list.changeQuery(qInput.trim());
          }}
          onBlur={() => list.changeQuery(qInput.trim())}
          sx={{ width: 260, '& .MuiInputBase-input': { fontSize: 13 } }}
        />
        <Tooltip title="再読み込み">
          <IconButton size="small" onClick={list.reload}>
            <Icons.Refresh />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Fab variant="extended" size="medium" sx={STANDARD_FAB_CTA_SX} onClick={() => navigate('/mail-delivery/campaigns/new')}>
          <Icons.PostAdd sx={{ mr: 1 }} />
          新規配信作成
        </Fab>
      </Box>
      {list.error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {list.error}
        </Alert>
      )}
      <Box sx={TABLE_FILL_SX}>
        <StandardDataTable
          rows={list.rows}
          columns={columns}
          loading={list.loading}
          loadingMessage="配信履歴を取得中"
          totalCount={list.total}
          page={list.page}
          pageSize={list.pageSize}
          onPageChange={list.setPage}
          onPageSizeChange={list.changePageSize}
          onSortChange={() => {}}
        />
      </Box>
    </Box>
  );
};

export default MailDeliveryCampaigns;
